/**
 * QuizRunner — renders quizzes using shared-learning QuizRenderer directly.
 * - Full quiz UI always rendered (no summary card on re-entry).
 * - Lesson mode submitted re-entry: solveResult passed → correctness + description visible.
 * - Test mode submitted re-entry: initialAnswers only → selected answer visible, no correctness.
 *   User can click "답 수정하기" to re-enter edit mode.
 * - onQuizResponse → LC learning session API (quiz_response_progress INSERT).
 */
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  QuizRenderer,
  KatexText,
  QuizStyleProvider,
  QuizResultPopup,
  isConsecutiveThree,
  type StreakEntry,
} from '@quebon/shared-learning'
import type { QuizScript, QuizSolveResult } from '@quebon/shared-learning'
import {
  submitQuizResponse,
  submitOfflineQuizInput,
  completeSession,
  type LcQuizDataResponse,
  type LcQuizResultDescription,
  type LcQuizResponseResult,
} from '@/api/lcLearning'
import {
  submitLibraryResponse,
  completeLibrarySession,
  type LcLibrarySubmitResponse,
} from '@/api/lcLibraryReview'
import type { RuntimeMode } from './ComponentRunner'
import { Card } from './ui/Card'
import { Button } from './ui/Button'
import { Button3D } from './ui/Button3D'
import { LibraryQuizCompletionDialog } from './LibraryQuizCompletionDialog'
import { Mascot } from './ui/Mascot'
import { QuizNavChips } from './QuizNavChips'
import { QuizNavArrows } from './QuizNavArrows'
import { quizActionModel } from './lib/quizReviewModel'

interface QuizResultInfo {
  isCorrect: boolean | null
  corrects?: (boolean | null)[]
  correctAnswers?: unknown[]
  description?: LcQuizResultDescription | null
}

interface Props {
  componentProgressId: number | null
  sessionId: number | null
  enrollmentId: number
  componentType: string
  quizData: LcQuizDataResponse
  runtimeMode?: RuntimeMode
  onComplete: () => void
  onRetryRequired?: () => void
  exitConfirmationRequested?: boolean
  onCompletionReadyChange?: (ready: boolean) => void
  onExitWithoutComplete?: () => void
}

// Convert backend quiz map to QuizScript
// Backend returns full QuizInfoView via objectMapper — cast directly
function toQuizScript(quiz: Record<string, unknown>): QuizScript {
  return {
    ...quiz,
    quizId: String(quiz.quizId ?? quiz.id ?? ''),
    scriptId: quiz.scriptId != null ? String(quiz.scriptId) : undefined,
    type: String(quiz.type ?? 'multi_choice'),
    importance: (quiz.importance as number) ?? 0,
    version: (quiz.version as number) ?? 1,
  } as QuizScript
}

function areStringArraysEqual(left: string[] | undefined, right: string[]): boolean {
  return left?.length === right.length && left.every((value, index) => value === right[index])
}

export default function QuizRunner({
  componentProgressId,
  sessionId,
  enrollmentId,
  componentType,
  quizData,
  runtimeMode = 'live',
  onComplete,
  onRetryRequired,
  exitConfirmationRequested = false,
  onCompletionReadyChange,
  onExitWithoutComplete,
}: Props) {
  const isLive = runtimeMode === 'live'

  const quizzes = useMemo(() => quizData.quizzes.map(toQuizScript), [quizData.quizzes])
  const [currentIndex, setCurrentIndex] = useState(quizData.resumeFromIndex ?? 0)

  const [submittedAnswersByQuizId, setSubmittedAnswersByQuizId] = useState<Map<string, unknown[]>>(() => {
    const map = new Map<string, unknown[]>()
    for (const r of quizData.previousResponses ?? []) {
      map.set(r.quizId, r.submittedResponse)
    }
    return map
  })
  const [resultsByQuizId, setResultsByQuizId] = useState<Map<string, QuizResultInfo>>(() => {
    const map = new Map<string, QuizResultInfo>()
    for (const r of quizData.previousResponses ?? []) {
      map.set(r.quizId, {
        isCorrect: r.isCorrect,
        corrects: r.corrects,
        correctAnswers: r.correctAnswers,
        description: r.description,
      })
    }
    return map
  })

  // 입력 중이지만 아직 제출 안 된 답변 — QuizRenderer.onAnswerChange 콜백으로 추적.
  const [pendingAnswersByQuizId, setPendingAnswersByQuizId] = useState<Map<string, string[]>>(new Map())

  // 이미 정오답 사운드를 재생한 quizId. 재진입(previousResponses)은 처음부터 포함.
  const [soundPlayedQuizIds, setSoundPlayedQuizIds] = useState<Set<string>>(() => {
    const set = new Set<string>()
    for (const r of quizData.previousResponses ?? []) set.add(r.quizId)
    return set
  })

  const [completing, setCompleting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hintExpanded, setHintExpanded] = useState(false)
  const [popup, setPopup] = useState<null | { isCorrect: boolean; isCombo: boolean }>(null)
  const [completionConfirmationRequested, setCompletionConfirmationRequested] = useState(false)
  const { t } = useTranslation('learner')

  const currentQuiz = quizzes[currentIndex]

  const answeredIndices = useMemo(() => {
    const set = new Set<number>()
    quizzes.forEach((q, i) => {
      if (q.quizId && submittedAnswersByQuizId.has(q.quizId)) set.add(i)
    })
    return set
  }, [quizzes, submittedAnswersByQuizId])

  const isTestMode = quizData.solveMode === 'test'
  const isReview = quizData.isReview === true
  const canResubmit = quizData.canResubmit === true
  // G8 — 오프라인 입력 단계(채점 보류). 라이브 세션에서만 활성(엣지: 도서관 복습 경로는 기존 자동채점).
  const isOfflineInput = isLive && quizData.offlineInput === true

  const currentQuizId = currentQuiz?.quizId ?? ''
  const isCurrentSubmitted = submittedAnswersByQuizId.has(currentQuizId)

  const action = quizActionModel({ isReview, canResubmit, isTestMode, isCurrentSubmitted })

  // 문제 이동 시 힌트 자동 접힘
  useEffect(() => {
    setHintExpanded(false)
  }, [currentQuizId])
  const allSubmitted = quizzes.every(q => q.quizId ? submittedAnswersByQuizId.has(q.quizId) : false)
  const canComplete = allSubmitted
  const canGoNext = isTestMode
    ? currentIndex < quizzes.length - 1
    : currentIndex < quizzes.length - 1 && isCurrentSubmitted
  const canGoPrev = currentIndex > 0

  useEffect(() => {
    onCompletionReadyChange?.(allSubmitted)
    return () => onCompletionReadyChange?.(false)
  }, [allSubmitted, onCompletionReadyChange])

  // 현재 제출된 답(있으면) → 없으면 pending 답(있으면) → undefined
  // string[]으로 변환 (QuizRenderer는 string[]을 기대)
  const currentInitialAnswers = useMemo((): string[] | undefined => {
    const submitted = submittedAnswersByQuizId.get(currentQuizId)
    if (submitted) return submitted.map(a => String(a))
    const pending = pendingAnswersByQuizId.get(currentQuizId)
    if (pending && pending.length > 0) return pending
    return undefined
  }, [currentQuizId, submittedAnswersByQuizId, pendingAnswersByQuizId])

  // solveResult 전달 정책:
  // - lesson 모드 + 제출됨: 결과 표시 (정오답 색, 해설)
  // - test 모드: 항상 null (세션 중에는 정답 숨김)
  const currentSolveResult = useMemo((): QuizSolveResult | null => {
    if (isTestMode) return null
    const result = resultsByQuizId.get(currentQuizId)
    if (!result) return null
    return {
      correct: result.isCorrect,
      corrects: (result.corrects ?? []).map(c => c == null ? null : Boolean(c)),
      correctAnswers: (result.correctAnswers ?? []).map(a => String(a)),
      description: result.description ?? undefined,
    }
  }, [isTestMode, currentQuizId, resultsByQuizId])

  // G3 — 오답 해설 숨김 정책 (config-v2 answerExplanationPolicy).
  // hide_on_incorrect + 현재 문제 오답일 때만 해설을 억제(정답은 해설 노출).
  // 엣지④: 도서관 복습(library-review)·인세션 오답풀기 경로는 해설 노출 유지 → isLive 가드.
  const applyHidePolicy = isLive && quizData.answerExplanationPolicy === 'hide_on_incorrect'
  const hideExplanation = applyHidePolicy && currentSolveResult?.correct === false

  // G4 — 선택지 텍스트 흐리기(1회차 한정). 백엔드가 isFirstAttempt 게이트 후 'dimmed' 전달하므로
  // FE는 정책값만 반영(복습·도서관 경로는 백엔드에서 이미 'show'). multi_choice·inline 렌더러만 반응.
  const dimChoiceText = quizData.choiceDisplayPolicy === 'dimmed'

  const handleAnswerChange = useCallback((answers: string[]) => {
    setPendingAnswersByQuizId(prev => {
      if (areStringArraysEqual(prev.get(currentQuizId), answers)) return prev
      return new Map(prev).set(currentQuizId, answers)
    })
  }, [currentQuizId])

  // QuizRenderer의 내장 제출 버튼에서 호출됨.
  const handleSolve = useCallback(async (
    _quizId: string | number,
    answers: string[],
    vars: Record<string, unknown>,
    elapsedTime: number,
  ) => {
    // live·library-review 모두 세션 기록형이므로 sessionId 필수.
    if (!sessionId) {
      setError('세션 정보가 없습니다.')
      return
    }
    if (submitting) return
    setSubmitting(true)
    setError(null)

    // G8 — 오프라인 입력 단계(1회차 교재 풀이): 자동채점 대신 제출만 기록(채점 보류).
    // isCorrect=null 로 저장되며 강사가 코칭 콘솔에서 채점한다. 결과/콤보 팝업 없음.
    if (isOfflineInput) {
      try {
        await submitOfflineQuizInput(sessionId, {
          quizId: currentQuizId,
          answers,
          vars: null,
          timeSpentSec: Math.floor(elapsedTime / 1000),
        })
        setSubmittedAnswersByQuizId(prev => new Map(prev).set(currentQuizId, answers))
        setPendingAnswersByQuizId(prev => {
          const next = new Map(prev)
          next.delete(currentQuizId)
          return next
        })
      } catch (e) {
        console.error('submitOfflineQuizInput failed', e)
        setError(t('quiz.offlineInput.submitError'))
      } finally {
        setSubmitting(false)
      }
      return
    }

    try {
      const result: LcQuizResponseResult = isLive
        ? await submitQuizResponse(sessionId, {
            quizId: currentQuizId,
            answers,
            vars: null,
            timeSpentSec: Math.floor(elapsedTime / 1000),
          })
        : adaptLibrarySubmit(
            await submitLibraryResponse(sessionId, {
              quizId: currentQuizId,
              submittedResponse: Object.fromEntries(
                answers.map((answer, index) => [String(index), answer]),
              ),
              varInstantiation: vars ?? undefined,
              scriptId: currentQuiz?.scriptId ?? undefined,
              timeSpentSec: Math.floor(elapsedTime / 1000),
            }),
            currentQuizId,
          )

      setSubmittedAnswersByQuizId(prev => new Map(prev).set(currentQuizId, answers))
      setResultsByQuizId(prev => new Map(prev).set(currentQuizId, {
        isCorrect: result.isCorrect,
        corrects: result.corrects,
        correctAnswers: result.correctAnswers,
        description: result.description ?? undefined,
      }))
      // 제출 완료 시 pending 정리
      setPendingAnswersByQuizId(prev => {
        const next = new Map(prev)
        next.delete(currentQuizId)
        return next
      })

      // 자동채점 결과가 있는 문제만 정오답/콤보 팝업을 표시한다.
      // 서술형(null)은 선생님 채점 전까지 중립적인 제출 완료 상태를 유지한다.
      if (result.isCorrect !== null) {
        const projectedStreak: StreakEntry[] = quizzes.map((q, i) => {
          const qid = q.quizId ?? ''
          if (i === currentIndex) return result.isCorrect
          const r = resultsByQuizId.get(qid)
          return r ? r.isCorrect : null
        })
        setPopup({
          isCorrect: result.isCorrect,
          isCombo: isConsecutiveThree(projectedStreak, currentIndex),
        })
      } else {
        setPopup(null)
      }
    } catch (e) {
      console.error('submitQuizResponse failed', e)
      setError('답변 제출 중 오류가 발생했습니다.')
    } finally {
      setSubmitting(false)
    }
  }, [isLive, isOfflineInput, sessionId, currentQuiz, currentQuizId, submitting, quizzes, currentIndex, resultsByQuizId, t])

  const navigateTo = useCallback((newIndex: number) => {
    if (newIndex < 0 || newIndex >= quizzes.length) return
    // 이동 전 현재 퀴즈가 풀렸으면 사운드 재생 완료 표시 → 재진입 시 사운드 재생 방지
    if (resultsByQuizId.has(currentQuizId)) {
      setSoundPlayedQuizIds(prev => {
        if (prev.has(currentQuizId)) return prev
        return new Set(prev).add(currentQuizId)
      })
    }
    setCurrentIndex(newIndex)
  }, [quizzes.length, currentQuizId, resultsByQuizId])

  const handlePopupClose = useCallback(() => setPopup(null), [])

  const handleEditSubmitted = useCallback((quizId: string) => {
    setSubmittedAnswersByQuizId(prev => {
      const next = new Map(prev)
      next.delete(quizId)
      return next
    })
    setResultsByQuizId(prev => {
      const next = new Map(prev)
      next.delete(quizId)
      return next
    })
  }, [])

  const handleCompleteSession = useCallback(async () => {
    setCompleting(true)
    setError(null)
    try {
      let completionResult: Awaited<ReturnType<typeof completeSession>> | null = null
      if (isLive && componentProgressId != null && !action.skipCompleteApi) {
        completionResult = await completeSession(componentProgressId, {
          enrollmentId,
          componentType,
          passScore: quizData.passingScore,
          maxAttempts: quizData.maxAttempts,
        })
      } else if (!isLive && sessionId != null) {
        // 도서관 격리 세션 완료 — canonical 진도 미변경
        await completeLibrarySession(sessionId, quizData.passingScore)
      }

      if (
        completionResult?.canRetry === true &&
        completionResult.componentStatus !== 'completed'
      ) {
        setError('통과 기준에 도달하지 못했어요. 다시 풀어주세요.')
        onRetryRequired?.()
        return
      }

      onComplete()
    } catch (e) {
      console.error('completeSession failed', e)
      setError('완료 처리 중 오류가 발생했습니다.')
    } finally {
      setCompleting(false)
    }
  }, [
    isLive,
    action.skipCompleteApi,
    componentProgressId,
    sessionId,
    enrollmentId,
    componentType,
    quizData.passingScore,
    quizData.maxAttempts,
    onComplete,
    onRetryRequired,
  ])

  const handleCompleteClick = useCallback(() => {
    if (runtimeMode === 'library-review') {
      setCompletionConfirmationRequested(true)
      return
    }
    void handleCompleteSession()
  }, [handleCompleteSession, runtimeMode])

  if (quizzes.length === 0) {
    return (
      <QuizStyleProvider variant="lc">
        <Card size="md" className="text-center py-10 text-lc-gray-600 text-[18px] tracking-[-0.6px]">
          퀴즈 데이터가 없습니다.
        </Card>
      </QuizStyleProvider>
    )
  }

  if (completing) {
    return (
      <QuizStyleProvider variant="lc">
        <div className="flex flex-col items-center justify-center gap-4 py-20">
          <Mascot name="queda" shape="circle" size={80} className="animate-bounce" />
          <p className="text-[18px] leading-[1.5] tracking-[-0.6px] font-normal text-lc-gray-600">
            완료 처리 중...
          </p>
        </div>
      </QuizStyleProvider>
    )
  }

  // test 모드에서 이미 제출된 문제는 QuizRenderer의 내장 제출 버튼을 숨기고,
  // 대신 "답 수정하기" 버튼을 외부에 노출한다.
  // lesson 모드 제출 시엔 solveResult가 전달되어 내장 로직이 자동으로 숨김.
  // G8 오프라인 입력은 solveResult가 없으므로 제출 후 명시적으로 숨김.
  const hideBuiltinSubmit = action.hideBuiltinSubmit || (isOfflineInput && isCurrentSubmitted)

  // 힌트 노출 조건 (spec §3.1): showHint 옵션 on + 현재 퀴즈 hint 문자열이 비어있지 않음
  const hintText = currentQuiz?.hint?.trim() ?? ''
  const canShowHint = quizData.showHint === true && hintText.length > 0

  return (
    <QuizStyleProvider variant="lc">
      <div className="flex h-auto min-h-[520px] w-full flex-col items-end gap-5">
        <section className="relative flex min-h-[430px] w-full flex-1 flex-col overflow-visible rounded-[20px] bg-gradient-to-r from-white to-[#f9fafb] px-5 py-6 shadow-[0_4px_8px_0_rgb(0_0_0/0.05)]">
          <QuizNavChips
            total={quizzes.length}
            currentIndex={currentIndex}
            answeredIndices={answeredIndices}
            solveMode={quizData.solveMode}
            quizIds={quizzes.map(q => q.quizId ?? '')}
            resultsByQuizId={resultsByQuizId}
            lockBeyondAnswered={!isTestMode}
            className="absolute right-5 top-4 z-10 max-w-[calc(100%-40px)]"
            onSelect={navigateTo}
          />

          <div className="mx-auto flex w-full max-w-[960px] flex-col gap-4">
            {error ? (
              <div
                role="alert"
                className="flex items-center gap-3 rounded-[10px] border border-lc-error-100 bg-lc-error-50 px-4 py-3"
              >
                <div
                  aria-label={t('aria.error')}
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-lc-error-500 text-[18px] font-bold text-white"
                >
                  ✕
                </div>
                <p className="text-[16px] leading-[1.5] text-lc-text-error">{error}</p>
              </div>
            ) : null}

            {currentQuiz ? (
              <div className="flex flex-col gap-4 pt-0">
                {canShowHint ? (
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => setHintExpanded(prev => !prev)}
                      className="inline-flex h-8 items-center gap-1.5 self-start rounded-full bg-lc-key-50 px-3 text-[14px] font-medium leading-none text-lc-key-700 transition-colors hover:bg-lc-key-100"
                      aria-expanded={hintExpanded}
                    >
                      <span aria-hidden>💡</span>
                      {hintExpanded ? t('quiz.hint.hide') : t('quiz.hint.show')}
                    </button>
                    {hintExpanded ? (
                      <div className="rounded-[10px] bg-lc-key-50 p-4 text-[16px] leading-[1.6] text-lc-text-primary">
                        <KatexText text={hintText} />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <QuizRenderer
                  key={`${currentQuizId}-${isCurrentSubmitted ? 'submitted' : 'open'}`}
                  quizType={currentQuiz.type ?? 'multi_choice'}
                  quizId={currentQuizId}
                  quiz={currentQuiz}
                  onSolve={handleSolve}
                  solveResult={currentSolveResult}
                  initialAnswers={currentInitialAnswers}
                  onAnswerChange={handleAnswerChange}
                  hideSubmitButton={hideBuiltinSubmit}
                  isViewSolved={hideBuiltinSubmit}
                  disableSound={soundPlayedQuizIds.has(currentQuizId)}
                  hideExplanation={hideExplanation}
                  dimChoiceText={dimChoiceText}
                />

                {isOfflineInput ? (
                  <div className="rounded-[10px] bg-lc-key-50 px-4 py-3 text-[15px] leading-[1.5] text-lc-key-700">
                    {isCurrentSubmitted ? t('quiz.offlineInput.submitted') : t('quiz.offlineInput.notice')}
                  </div>
                ) : null}

                {hideExplanation ? (
                  <div className="rounded-[10px] bg-lc-v3-gray-100 px-4 py-3 text-[15px] leading-[1.5] text-lc-text-secondary">
                    {t('quiz.explanationHidden')}
                  </div>
                ) : null}

                {action.showEditButton ? (
                  <div className="flex justify-end">
                    <Button3D variant="lightblue" size="M" onClick={() => handleEditSubmitted(currentQuizId)}>
                      {t('learn.quiz.editAnswer')}
                    </Button3D>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <QuizNavArrows
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            onPrev={() => navigateTo(currentIndex - 1)}
            onNext={() => navigateTo(currentIndex + 1)}
          />
        </section>

        {canComplete ? (
          <div className="flex w-40 flex-col items-center pt-3">
            <Button
              variant="navy"
              size="XL"
              className="w-full"
              onClick={handleCompleteClick}
            >
              {t('learn.quiz.complete')}
            </Button>
          </div>
        ) : null}
      </div>

      <QuizResultPopup
        isOpen={popup !== null}
        isCorrect={popup?.isCorrect ?? false}
        isCombo={popup?.isCombo ?? false}
        onClose={handlePopupClose}
        disableSound={soundPlayedQuizIds.has(currentQuizId)}
      />

      <LibraryQuizCompletionDialog
        open={
          runtimeMode === 'library-review' &&
          allSubmitted &&
          (exitConfirmationRequested || completionConfirmationRequested)
        }
        pending={completing}
        onComplete={() => void handleCompleteSession()}
        onKeepProgress={() => onExitWithoutComplete?.()}
      />
    </QuizStyleProvider>
  )
}

/**
 * library-review 제출 응답을 live-session 의 LcQuizResponseResult 모양으로 변환.
 * 격리 세션에 기록되지만 responseId 는 노출하지 않으므로 'library-review' sentinel 사용.
 */
function adaptLibrarySubmit(
  r: LcLibrarySubmitResponse,
  quizId: string,
): LcQuizResponseResult {
  return {
    responseId: 'library-review',
    quizId,
    isCorrect: r.isCorrect,
    corrects: r.corrects,
    correctAnswers: r.correctAnswers,
    description: r.description ?? null,
    partialCredit: r.partialCredit,
  }
}
